import { useContext } from 'react';
import { useParams } from "react-router-dom";
import * as S from './style';
import { Grid, Radio, RadioGroup, FormControlLabel, Checkbox, CircularProgress, Input, Box, FormHelperText } from '@mui/material';
import { useFormik } from 'formik';
import Modal from 'components/Modal/Modal';
import * as Yup from "yup";
import "assets/theme/custom-flatpickr.css";
import 'moment/locale/pt-br';
import showToast from 'components/Toast/Toast';
import { TokenContext } from "context/TokenContext";
import { requestCalendarDify } from 'utils/requests/dify';
import { createCalendar, createCalendarWeek, createWeekPost, deleteCalendar } from 'utils/requests/communities';
import { CustomButton } from 'components/ButtonCustom/style';
import { useState } from 'react';

const contentFormats = [
  { id: 'desafiosSemanais', label: 'Desafios semanais' },
  { id: 'enquetes', label: 'Enquetes' },
  { id: 'bastidoresDoCriador', label: 'Bastidores do criador' },
  { id: 'livesAuloes', label: 'Lives ou aulões' },
  { id: 'conteudoPersonalizado', label: 'Outro' }
];

const contentTone = [
  { id: 'inspirador', label: 'Inspirador' },
  { id: 'profundoReflexivo', label: 'Profundo/reflexivo' },
  { id: 'informativo', label: 'Informativo' },
  { id: 'divertido', label: 'Divertido' },
  { id: 'tomPersonalizado', label: 'Outro' }
];

const contentFrequency = [
  { id: 'todoDia', label: 'Todo dia' },
  { id: 'umaVezPorSemana', label: '1x por semana' },
  { id: 'duasVezesPorSemana', label: '2x por semana' },
  { id: 'tresVezesPorSemana', label: '3x por semana' },
  { id: 'quatroVezesPorSemana', label: '4x por semana' },
  { id: 'cincoVezesPorSemana', label: '5x por semana' },
];

const BuildCalendar = ({ communityData = {}, initialData, refetch, onSubmitCalendarSuccess = () => {}, isReprogramming = false, calendarId }) => {
  const { decodedToken } = useContext(TokenContext);
  const role = decodedToken?.type;
  const { communityId } = useParams();
  const [openConfirmReprogramModal, setOpenConfirmReprogramModal] = useState(false);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      tone: '',
      customTone: '',
      frequency: '',
      contentFormats: [],
      customContentFormat: '',
    },
    validationSchema: Yup.object({
      tone: Yup.string().required('O tom da comunidade é obrigatório'),
      customTone: Yup.string().when('tone', {
        is: 'tomPersonalizado',
        then: (schema) => schema.required('O tom personalizado é obrigatório'),
      }),
      frequency: Yup.string().required('A frequência de conteúdo é obrigatório'),
      contentFormats: Yup.array().min(1, 'Selecione pelo menos um formato de conteúdo'),
      customContentFormat: Yup.string().when('contentFormats', {
        is: (contentFormats) => contentFormats && contentFormats.includes('conteudoPersonalizado'),
        then: (schema) => schema.required('O formato de conteúdo personalizado é obrigatório'),
      }),
    }),
    onSubmit: async (values) => {
      try {
        const payload = {
          ...values,
          purpose: communityData.plan_details.purpose,
          name: communityData.plan_details.name,
          tagline: communityData.plan_details.tagline,
          platform: communityData.plan_details.platform,
          subscription: communityData.plan_details.subscription,
          specialInfo: communityData.plan_details.specialInfo,
          content: communityData.plan_details.content,
          contentTypes: communityData.plan_details.contentTypes,
          communicationTypes: communityData.plan_details.communicationTypes,
        };
        
        const chatbotResponse = await requestCalendarDify(payload);
        if (!chatbotResponse?.answer) {
          const errorMsg = chatbotResponse?.rawMessage?.[0]?.split(': ')[1] || chatbotResponse?.message;
          throw new Error(errorMsg || "Resposta do chatbot inválida");
        }
        const difyResponse = chatbotResponse.answer;

        if (isReprogramming && calendarId) {
          await deleteCalendar(calendarId);
        }

        const allTemplates = [];
        Object.keys(difyResponse).forEach(weekKey => {
          if (weekKey.startsWith('week') && difyResponse[weekKey]?.templates) {
            allTemplates.push(...difyResponse[weekKey].templates);
          }
        });

        const calendarPayload = {
          community_id: communityId,
          calendar_data: JSON.stringify(difyResponse),
          week_template: JSON.stringify(allTemplates)
        };

        const calendarResponse = await createCalendar(calendarPayload);
        const calendarIdToUse = calendarResponse.id;

        const weekCreationPromises = [];
        for (const [weekKey, weekData] of Object.entries(difyResponse)) {
          if (weekKey.startsWith('week')) {
            const weekPayload = {
              calendar_id: calendarIdToUse,
              week_description: weekKey,
              week_data: JSON.stringify(weekData)
            };
            weekCreationPromises.push(createCalendarWeek(weekPayload).then(res => ({[weekKey]: res})));
          }
        }

        const weekResponsesResults = await Promise.all(weekCreationPromises);
        const weekResponses = weekResponsesResults.reduce((acc, current) => ({ ...acc, ...current }), {});

        const postCreationPromises = [];
        for (const [weekKey, weekData] of Object.entries(difyResponse)) {
          if (weekKey.startsWith('week') && weekData.posts && Array.isArray(weekData.posts)) {
            const weekId = weekResponses[weekKey].id;
            for (const post of weekData.posts) {
              const postPayload = {
                calendar_week_id: weekId,
                post_title: post.title,
                script_data: JSON.stringify({
                  ...post,
                  completed: false
                })
              };
              postCreationPromises.push(createWeekPost(postPayload));
            }
          }
        }
        await Promise.all(postCreationPromises);

        showToast(isReprogramming ? "Calendário reprogramado com sucesso!" : "Calendário de conteúdo gerado com sucesso!", "success"); 
        refetch();
      } catch (error) {
        const errorMsg = error?.rawMessage?.[0]?.replace(/^Error:\\s*/, '') || error?.message;
        showToast(
          errorMsg
            ? `Erro ao ${isReprogramming ? 'reprogramar' : 'gerar'} o calendário: ${errorMsg}`
            : `Erro ao ${isReprogramming ? 'reprogramar' : 'gerar'} o calendário.`,
          "error"
        );
      }
    }
  });

  const handleContentTypeChange = (typeId) => {
    const currentFormats = [...formik.values.contentFormats];
    const index = currentFormats.indexOf(typeId);
    
    if (index === -1) {
      currentFormats.push(typeId);
    } else {
      currentFormats.splice(index, 1);
    }
    
    formik.setFieldValue('contentFormats', currentFormats);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isReprogramming) {
      setOpenConfirmReprogramModal(true);
    } else {
      formik.handleSubmit();
    }
  };

  const content = (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Grid container justifyContent='space-between'>
          <S.FormTitleGrid container flexDirection='column' item md={7}>
            <p>Calendário</p>
            <p className='subTitle'>Plano de conteúdo para 30 dias</p>
          </S.FormTitleGrid>
        </Grid>
      </Box>
      <Grid container>
        <S.CalendarTitleGrid container flexDirection='column' item md={12} xl={12}>
          <p>{isReprogramming ? 'Vamos reprogramar seu calendário de conteúdo?' : 'Vamos criar seu calendário de conteúdo?'}</p>
        </S.CalendarTitleGrid>
      </Grid>
      <form onSubmit={handleSubmit}>
        <S.FormGrid container flexDirection='column' gap='30px' mt='50px'>
          <Grid container>
            <Grid item md={12} xl={12}>
              <S.SectionTitle>Qual o tom da comunidade que você quer construir?</S.SectionTitle>
              <RadioGroup
                name="tone"
                value={formik.values.tone}
                onChange={formik.handleChange}
                row
                sx={{ gap: '15px' }}
              >
                {contentTone.map((tone) => (
                  <FormControlLabel 
                    key={tone.id}
                    value={tone.id} 
                    control={<Radio />} 
                    label={tone.label} 
                    sx={{ color: 'white' }}
                  />
                ))}
                {formik.values.tone === "tomPersonalizado" && (
                  <Input 
                    name="customTone"
                    value={formik.values.customTone || ''}
                    onChange={formik.handleChange}
                    placeholder="Qual?"
                    sx={{ color: '#FFFFFF', ml: 2, width: '250px', border: '1px solid #FFFFFF' }}
                  />
                )}
              </RadioGroup>
              {formik.touched.tone && formik.errors.tone && (
                <FormHelperText error sx={{ color: '#f44336', mt: 1 }}>
                  {formik.errors.tone}
                </FormHelperText>
              )}
              {formik.values.tone === "tomPersonalizado" && formik.touched.customTone && formik.errors.customTone && (
                <FormHelperText error sx={{ color: '#f44336', mt: 1 }}>
                  {formik.errors.customTone}
                </FormHelperText>
              )}
            </Grid>
          </Grid>
          <Grid container>
            <Grid item md={12} xl={12}>
              <S.SectionTitle>Qual a frequência ideal de conteúdo?</S.SectionTitle>
              <RadioGroup
                name="frequency"
                value={formik.values.frequency}
                onChange={formik.handleChange}
                row
                sx={{ gap: '15px' }}
              >
                {contentFrequency.map((frequency) => (
                  <FormControlLabel 
                    key={frequency.id}
                    value={frequency.id} 
                    control={<Radio />} 
                    label={frequency.label} 
                    sx={{ color: 'white' }}
                  />
                ))}
              </RadioGroup>
              {formik.touched.frequency && formik.errors.frequency && (
                <FormHelperText error sx={{ color: '#f44336', mt: 1 }}>
                  {formik.errors.frequency}
                </FormHelperText>
              )}
            </Grid>
          </Grid>
          <Grid container>
            <Grid item md={12} xl={12}>
              <S.SectionTitle>Quer incluir algum desses formatos de conteúdo?</S.SectionTitle>
              <Grid container spacing={2} gap='10px'>
                {contentFormats.map((format) => (
                  <Grid item key={format.id}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={formik.values.contentFormats.includes(format.id)}
                          onChange={() => handleContentTypeChange(format.id)}
                          name={`contentFormat-${format.id}`}
                        />
                      }
                      label={format.label}
                      sx={{ color: 'white' }}
                    />
                  </Grid>
                ))}
                {formik.values.contentFormats.includes('conteudoPersonalizado') && (
                  <Input 
                    name="customContentFormat"
                    value={formik.values.customContentFormat || ''}
                    onChange={formik.handleChange}
                    placeholder="Qual?"
                    sx={{ 
                      color: '#FFFFFF', 
                      mt: 2, 
                      width: '250px', 
                      border: '1px solid #FFFFFF',
                      marginLeft: '10px'
                    }}
                  />
                )}
                
              </Grid>
              {formik.touched.contentFormats && formik.errors.contentFormats && (
                <FormHelperText error sx={{ color: '#f44336', mt: 1 }}>
                  {formik.errors.contentFormats}
                </FormHelperText>
              )}
              {formik.values.contentFormats.includes('conteudoPersonalizado') && formik.touched.customContentFormat && formik.errors.customContentFormat && (
                  <FormHelperText error sx={{ color: '#f44336', mt: 1 }}>
                    {formik.errors.customContentFormat}
                  </FormHelperText>
                )}
            </Grid>
          </Grid>

          <Grid container justifyContent='flex-end' mt={2} gap={2}>
            {initialData && initialData.calendarData && Object.keys(initialData.calendarData).length > 0 && (
              <Grid item>
                <CustomButton
                  variant="outlined"
                  onClick={() => onSubmitCalendarSuccess()}
                  sx={{ background: "linear-gradient(180deg, #818181 0%, #2a2020 155.15%)" }}
                >
                  Cancelar
                </CustomButton>
              </Grid>
            )}
            <Grid item>
              <CustomButton 
                type="submit"
                disabled={formik.isSubmitting}
                sx={{
                  opacity: formik.isSubmitting ? 0.7 : 1,
                  cursor: formik.isSubmitting ? 'not-allowed' : 'pointer',
                }}
              >
                {formik.isSubmitting
                ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                : ''}
                {formik.isSubmitting ? (isReprogramming ? 'Reprogramando...' : 'Gerando calendário...') : (isReprogramming ? 'Reprogramar calendário de conteúdo' : 'Gerar calendário de conteúdo')}
              </CustomButton>
            </Grid>
          </Grid>
        </S.FormGrid>
      </form>
      {isReprogramming && (
        <Modal open={openConfirmReprogramModal} size="sm" onClose={() => setOpenConfirmReprogramModal(false)}>
          <Grid container flexDirection="column" gap="20px">
            <p style={{ fontSize: "24px" }}>Confirmar reprogramação</p>
            <p style={{ fontSize: "18px" }}>
              Esta ação irá reprogramar o calendário, apagando todo o progresso atual e gerando nova cobrança de tokens. Deseja continuar?
            </p>
            <Grid container gap="15px" justifyContent="center">
              <Grid item lg={3}>
                <CustomButton
                  variant="outlined"
                  onClick={() => setOpenConfirmReprogramModal(false)}
                  sx={{ background: "linear-gradient(180deg, #818181 0%, #2a2020 155.15%)" }}
                >
                  Cancelar
                </CustomButton>
              </Grid>
              <Grid item lg={3}>
                <CustomButton
                  onClick={() => {
                    setOpenConfirmReprogramModal(false);
                    formik.handleSubmit();
                  }}
                >
                  Confirmar
                </CustomButton>
              </Grid>
            </Grid>
          </Grid>
        </Modal>
      )}
    </>
  );
  return isReprogramming ? content : (
    <S.ProjectCard>
      {content}
    </S.ProjectCard>
  )
};

export default BuildCalendar;